import { handleActions } from 'redux-actions';
import { getFormValues } from 'redux-form';
import intl from '../shared/intl';
import checkPermissions from '../shared/check-permissions';
import http from '../shared/http';
import createFilters from '../shared/fixed-filters';
import endpoints from '../../config/endpoints';
import { default as createCalendar } from '../shared/calendar';
import moment from 'moment';
import qs from 'qs';
import _get from 'lodash/get';

import { regions } from '../../../../../enums/regions';
import csvExport from '../shared/csvExport';

// actions types
const FETCH_DATA_SUCCESS = 'top-offers-list/fetch-data-success';
const FETCH_DATA_ERROR = 'top-offers-list/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'top-offers-list/toggle-progress-bar';
const EXPORT_DATA_SUCCESS = 'top-offers-list/EXPORT_DATA_SUCCESS';

const PAGINATE = 'top-offers-list/PAGINATE';
const APPEND_DATA_SUCCESS = 'top-offers-list/APPEND_DATA_SUCCESS';
const DEFAULT_PAGINATION = 'top-offers-list/DEFAULT_PAGINATION';
const TOGGLE_IS_INCLUDED_NEW_OFFERS = 'top-offers-list/TOGGLE_IS_INCLUDED_NEW_OFFERS';

// permissions
export const allowedPermissions = {
	view: 'offers_list',
	viewAll: 'top_offers_view_all'
};

const calendar = createCalendar({
	namespace: 'topOffersList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(3 + 13, 'days').format('YYYY-MM-DD'),
		end: moment().subtract(3, 'days').format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'topOffersList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'admins',
			title: 'Manager',
			placeholder: 'Managers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS,
				params: 'only_managers=true&limit=100'
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS,
				params: `isTestSkipped=true`
			}
		},
		{
			name: 'country_ids',
			title: 'Countries',
			placeholder: 'Add country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC,
				params: 'limit=100'
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		},
		{
			name: 'niches',
			title: 'Niches',
			placeholder: 'Select niches',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_NICHES
			}
		},
		{
			name: 'traffic_source',
			title: 'Traffic source',
			placeholder: 'Select traffic source',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.traffic-sources'
			}
		},
		{
			name: 'sale_model',
			title: 'Sale model',
			placeholder: 'Select sale model',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.offer-sale-models'
			}
		}
	]
});

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	table: {
		columns: {
			offer_id: {
				index: 'offer_id',
				title: 'Offer id'
			},
			country: {
				index: 'country',
				title: 'Country'
			},
			offer: {
				index: 'offer',
				title: 'Offer'
			},
			niche: {
				index: 'niche',
				title: 'Niche'
			},
			average_approve: {
				index: 'average_approve',
				title: 'Average approve'
			},
			status: {
				index: 'status',
				title: 'Status'
			}
		}
	},
	pagination: {
		limit: 30,
		offset: 0
	},
	isIncludedNewOffers: false,
	filters: filters.initialState,
	calendar: calendar.initialState,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			const { filters: { selectedFilters }, pagination } = getState().topOffersList;
			const filters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						filters[filter.name] = values;
					}
				});
			}
			const { start, end } = selectors.getCalendar(getState()).value;
			const toOffersCheckboxForm = selectors.getTopOffersCheckbox(getState()) || {};

			const query = qs.stringify({
				filters,
				...pagination,
				are_new_offers_included: toOffersCheckboxForm.are_new_offers_included || '',
				date_range: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` }
			});
			dispatch({ type: TOGGLE_PROGRESS_BAR });

			return (http({
				url: `v2/top-offers?${query}`,
				method: 'get',
				actions: [ pagination.offset === 0 ? FETCH_DATA_SUCCESS : APPEND_DATA_SUCCESS, FETCH_DATA_ERROR ]
			}))(dispatch);
		},
		toggleIsIncludedNewOffers: () => ({
			type: TOGGLE_IS_INCLUDED_NEW_OFFERS
		}),
		exportCsv: () => (dispatch, getState) => {

			const headers = [
				'offer_id', 'country_code', 'offer', 'niche', 'approve_to_valid_percent', 'approve_to_total_percent',
				'status'
			];

			const { filters: { selectedFilters } } = getState().topOffersList;
			const filters = {};

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						filters[filter.name] = values;
					}
				});
			}
			const { start, end } = selectors.getCalendar(getState()).value;

			const query = qs.stringify({
				filters,
				is_hidden_leads: true,
				limit: 100000, offset: 0,
				date_range: { start: `${start}T00:00:00Z`, end: `${end}T23:59:59Z` }
			});

			return (http({
				url: `v2/top-offers?${query}`,
				method: 'get',
				actions: [ EXPORT_DATA_SUCCESS, FETCH_DATA_ERROR ]
			}))(dispatch)
				.then(action => {

					if (action.type === EXPORT_DATA_SUCCESS && _get(action, 'payload.data.data.length')) {
						const rows = _get(action, 'payload.data.data', [])
							.map(row => ({...row, approve_to_total_percent: `${row.approve_to_total_percent}%`}))
							.map(row => ({...row, approve_to_valid_percent: `${row.approve_to_valid_percent}%` }));

						const columns = rows.map(entity => headers.map(index => entity[index] || '-'));

						const rowsForExport = [].concat([headers], columns);
						csvExport('Top_offers', rowsForExport);
					}
				});
		},
		apply: () => dispatch => {
			dispatch({ type: DEFAULT_PAGINATION });
			dispatch(actions.fetchData());
		},
		showMoreRows: () => (dispatch, getState) => {
			dispatch({ type: PAGINATE });
			dispatch(actions.fetchData());
		}
	},
	filters.actions,
	calendar.actions
);

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data && action.payload.data.data,
			hasViewAllPermission: action.payload.data && action.payload.data.hasViewAllPermission,
			pagination: initialState.pagination,
			showProgressBar: false,
			error: false
		}),
		[TOGGLE_PROGRESS_BAR]: (state, action) => ({
			...state, showProgressBar: !state.showProgressBar
		}),
		[TOGGLE_IS_INCLUDED_NEW_OFFERS]: (state, action) => ({
			...state, isIncludedNewOffers: !state.isIncludedNewOffers
		}),
		[DEFAULT_PAGINATION]: (state, action) => ({ ...state, pagination: initialState.pagination }),
		[PAGINATE]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				offset: state.pagination.offset + state.pagination.limit
			}
		}),
		[APPEND_DATA_SUCCESS]: (state, action) => ({
			...state,
			showProgressBar: false,
			error: false,
			data: state.data && [...state.data].concat(action.payload.data && action.payload.data.data)
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({
			...state, showProgressBar: false, error: true
		})
	},
	filters.reducers,
	calendar.reducers
);

// reducers
export default handleActions(reducers, {...initialState});

// selectors
export const selectors = {
	getData: state => state.topOffersList.data,
	getIsIncludedNewOffers: state => state.topOffersList.isIncludedNewOffers,
	getIntl: state => intl(state),
	getTable: state => {
		const { table, data, hasViewAllPermission } = state.topOffersList;
		const rows = (data || [])
			.reduce((acc, val, index, arr) => {
				const isChangingFromActiveToInactive = val.is_active_offer
					&& _get(arr, `[${index + 1}].is_active_offer` ) === false;

				return isChangingFromActiveToInactive ? [...acc, val] : [...acc, val];
			}, []);

		return {
			headers: Object.values(table.columns),
			rows: rows,
			hasViewAllPermission,
			allowedOnlyAdminMails: rows[0] && rows[0].allowedOnlyAdminMails,
			rows_count: rows[0] && rows[0].rows_count
		};
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.topOffersList.filters,
		state => state
	)(state),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.topOffersList.calendar, state => state
	)(state),
	getProgressBar: state => state.topOffersList.showProgressBar,
	getTopOffersCheckbox: state => getFormValues('topOffersCheckbox')(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.topOffersList.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/top-offers/index.js